import { IDivergence } from "@/@types/divergence";
import { Tags } from "@/enum/tags";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { apiSlice } from "@/services/http";
import { numberToCurrency } from "@/utils/money";
import useKeyboardShortcut from "@/utils/useKeyboardShortcut";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader } from "components/ui/card";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { CloseConciliation } from "../../closeConciliation";
import { OPEN_CASH_REGISTERS_WARNING } from "../../partial-operator-balance/types";
import { calculateTotalDivergences } from "../utils/CalculateTotalDivergences";
import { columns } from "./columns";
import CustomCheckbox from "./components/custom-checkbox";
import Table from "./components/table";
import { TableContainer } from "./styles";

interface DivergenceRecordedProps {
  isCollapsibleOpen: boolean;
  divergences: IDivergence[];
  isLoading: boolean;
  isFetching?: boolean;
  hasOpenCashRegister?: boolean;
}

export default function DivergenceRecorded({
  isCollapsibleOpen,
  divergences,
  isFetching,
  hasOpenCashRegister = false,
}: DivergenceRecordedProps) {
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [wasCollapsibleOpen, setWasCollapsibleOpen] =
    useState<boolean>(isCollapsibleOpen);
  const { handleModal } = useDialogModal();
  const dispatch = useDispatch();

  if (wasCollapsibleOpen !== isCollapsibleOpen) {
    setWasCollapsibleOpen(isCollapsibleOpen);
    setIsConfirmed(false);
  }

  const isChecked: boolean = isCollapsibleOpen && isConfirmed;

  const filteredDivergences: IDivergence[] = divergences.filter(
    (divergence: IDivergence) =>
      (divergence.operator ?? "").trim() !== "" &&
      (divergence.register_code ?? "").trim() !== "",
  );

  const closeConciliation = () => {
    if (hasOpenCashRegister) return;

    dispatch(apiSlice.util.invalidateTags([Tags.CAN_CLOSE_CONCILIATION]));

    handleModal({
      isOpen: true,
      element: <CloseConciliation />,
      title: "Fechar Conciliação",
    });
  };

  useKeyboardShortcut("F7", closeConciliation, true);

  return (
    <Card className="w-full lg:w-1/3 min-w-0 shadow-none flex flex-col gap-2 justify-between items-center">
      <div className="w-full">
        <CardHeader>
          <div className="flex w-full justify-between items-center">
            <div className="flex gap-4 items-center font-bold text-[#2B0C1A] text-[14px]">
              Divergências registradas
            </div>
            <div className="text-[14px]">
              <span className="font-bold text-[#2B0C1A]">Total - </span>
              {numberToCurrency(calculateTotalDivergences(filteredDivergences ?? []))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="shadow-none">
          <div className="w-full h-full flex flex-col justify-between gap-4">
            <div style={{ width: "100%", overflowX: "auto" }}>
              <TableContainer>
                <Table data={filteredDivergences} columns={columns} isFetching={isFetching} />
              </TableContainer>
            </div>
          </div>
        </CardContent>
      </div>
      <div className="w-full p-6 flex flex-col items-center justify-center gap-2">
        <CustomCheckbox
          label="Declaro que conferi e validei todas as informações do VAR/DTEF. A conferência é obrigatória antes da finalização da conciliação."
          checked={isChecked}
          disabled={!isCollapsibleOpen}
          onChange={(checked: boolean) => {
            if (isCollapsibleOpen) {
              setIsConfirmed(checked);
            }
          }}
        />
        {hasOpenCashRegister && (
          <p className="text-xs text-red-700 text-center">
            {OPEN_CASH_REGISTERS_WARNING.blockedAction}
          </p>
        )}
        <Button
          className="w-full"
          onClick={closeConciliation}
          disabled={!isCollapsibleOpen || !isChecked || hasOpenCashRegister}
        >
          Fechar Conciliação (F7)
        </Button>
      </div>
    </Card>
  );
}
